import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '../context/authStore';
import { Mail, Phone, Building, Shield, Bell, Edit2, Save, X, Loader2, CheckCircle, AlertCircle, Key } from 'lucide-react';
import { usersAPI, authAPI } from '../services/api';

export default function ProfilePage() {
  const { user, refreshUser } = useAuthStore();
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showChangePassword, setShowChangePassword] = useState(false);

  // Fetch notification settings
  const { data: notificationsData, isLoading: notificationsLoading } = useQuery({
    // Not ['notifications']: the bell dropdown caches the notification list
    // under that key, and the two overwrote each other on this page.
    queryKey: ['notification-settings'],
    queryFn: () => usersAPI.getNotifications(),
  });

  const notifications = notificationsData?.data?.notifications || {};

  return (
    <div className="animate-fade-in max-w-2xl">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-neutral-900 dark:text-white">Profile</h1>
        <p className="text-neutral-600 dark:text-neutral-400">Manage your account settings and preferences</p>
      </div>

      {/* Profile Info Card */}
      <ProfileInfoCard
        user={user}
        isEditing={isEditingProfile}
        onEdit={() => setIsEditingProfile(true)}
        onCancel={() => setIsEditingProfile(false)}
        onSave={() => {
          setIsEditingProfile(false);
          refreshUser?.();
        }}
      />

      {/* Notification Settings Card */}
      <NotificationSettingsCard
        notifications={notifications}
        isLoading={notificationsLoading}
      />

      {/* Security Card */}
      <div className="card">
        <div className="card-header">
          <h2 className="font-semibold">Security</h2>
        </div>
        <div className="card-body space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-neutral-900 dark:text-white">Password</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {user?.authProvider === 'local'
                  ? 'Change your password'
                  : 'Managed by your SSO provider'
                }
              </p>
            </div>
            {user?.authProvider === 'local' && (
              <button
                className="btn-outline"
                onClick={() => setShowChangePassword(true)}
              >
                <Key className="w-4 h-4" /> Change Password
              </button>
            )}
          </div>

          <hr className="border-neutral-200 dark:border-neutral-700" />

          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-neutral-900 dark:text-white">Email Verification</p>
              <p className="text-sm text-neutral-500 dark:text-neutral-400">
                {user?.emailVerified
                  ? 'Your email has been verified'
                  : 'Please verify your email address'
                }
              </p>
            </div>
            <div className="flex items-center gap-2">
              {user?.emailVerified ? (
                <span className="badge-success flex items-center gap-1">
                  <CheckCircle className="w-3 h-3" /> Verified
                </span>
              ) : (
                <button className="btn-outline btn-sm">Resend Verification</button>
              )}
            </div>
          </div>

          {user?.isPrimaryContact && (
            <>
              <hr className="border-neutral-200 dark:border-neutral-700" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">Primary Contact</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">
                    You have elevated permissions for your organization
                  </p>
                </div>
                <span className="badge-primary flex items-center gap-1">
                  <Shield className="w-3 h-3" /> Primary
                </span>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}

// Profile Info Card with Edit Mode
function ProfileInfoCard({ user, isEditing, onEdit, onCancel, onSave }) {
  const [formData, setFormData] = useState({
    firstName: user?.firstName || '',
    lastName: user?.lastName || '',
    phone: user?.phone || '',
  });
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: (data) => usersAPI.updateProfile(data),
    onSuccess: () => {
      onSave();
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to update profile');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');
    mutation.mutate(formData);
  };

  const handleCancel = () => {
    setFormData({
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    });
    setError('');
    onCancel();
  };

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between">
        <h2 className="font-semibold">Account Information</h2>
        {!isEditing && (
          <button className="btn-sm btn-ghost" onClick={onEdit}>
            <Edit2 className="w-4 h-4" /> Edit
          </button>
        )}
      </div>
      <div className="card-body">
        {/* Avatar and Name */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-16 h-16 rounded-full bg-primary-500 flex items-center justify-center text-white text-2xl font-bold">
            {user?.firstName?.[0]}{user?.lastName?.[0]}
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
              {user?.firstName} {user?.lastName}
            </h3>
            <p className="text-neutral-500 dark:text-neutral-400">{user?.email}</p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {isEditing ? (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">First Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.firstName}
                  onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                />
              </div>
              <div>
                <label className="label">Last Name</label>
                <input
                  type="text"
                  className="input"
                  value={formData.lastName}
                  onChange={(e) => setFormData({ ...formData, lastName: e.target.value })}
                />
              </div>
            </div>

            <div>
              <label className="label">Phone Number</label>
              <input
                type="tel"
                className="input"
                placeholder="(555) 123-4567"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                <X className="w-4 h-4" /> Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <Save className="w-4 h-4" /> Save Changes
                  </>
                )}
              </button>
            </div>
          </form>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Email</p>
                <p className="font-medium text-neutral-900 dark:text-white">{user?.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Phone className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Phone</p>
                <p className="font-medium text-neutral-900 dark:text-white">{user?.phone || 'Not set'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Company ID</p>
                <p className="font-medium text-neutral-900 dark:text-white">{user?.cwCompanyId || 'N/A'}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Shield className="w-5 h-5 text-neutral-400" />
              <div>
                <p className="text-sm text-neutral-500 dark:text-neutral-400">Auth Provider</p>
                <p className="font-medium text-neutral-900 dark:text-white capitalize">{user?.authProvider || 'Local'}</p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Notification Settings Card
function NotificationSettingsCard({ notifications, isLoading }) {
  const queryClient = useQueryClient();
  const [settings, setSettings] = useState(notifications);
  const [hasChanges, setHasChanges] = useState(false);

  // Update local state when data loads
  useEffect(() => {
    if (notifications) {
      setSettings(notifications);
    }
  }, [notifications]);

  const mutation = useMutation({
    mutationFn: (data) => usersAPI.updateNotifications(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notification-settings'] });
      setHasChanges(false);
    },
  });

  const handleToggle = (key) => {
    const newSettings = { ...settings, [key]: !settings[key] };
    setSettings(newSettings);
    setHasChanges(true);
  };

  const handleSave = () => {
    mutation.mutate(settings);
  };

  const notificationOptions = [
    { key: 'emailNotifications', label: 'Email Notifications', description: 'Receive email notifications for account activity' },
    { key: 'invoiceReminders', label: 'Invoice Reminders', description: 'Get notified when invoices are due' },
    { key: 'ticketUpdates', label: 'Ticket Updates', description: 'Receive updates when your support tickets change status' },
    { key: 'quoteNotifications', label: 'Quote Notifications', description: 'Get notified about new and updated quotes' },
    { key: 'paymentReceipts', label: 'Payment Receipts', description: 'Receive email receipts for payments' },
    { key: 'newsletterOptIn', label: 'Newsletter', description: 'Subscribe to company news and updates' },
  ];

  return (
    <div className="card mb-6">
      <div className="card-header flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bell className="w-5 h-5 text-neutral-400" />
          <h2 className="font-semibold">Notification Settings</h2>
        </div>
        {hasChanges && (
          <button
            className="btn-sm btn-primary"
            onClick={handleSave}
            disabled={mutation.isPending}
          >
            {mutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <>
                <Save className="w-4 h-4" /> Save
              </>
            )}
          </button>
        )}
      </div>
      <div className="card-body">
        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
          </div>
        ) : (
          <div className="space-y-4">
            {notificationOptions.map((option) => (
              <div key={option.key} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-neutral-900 dark:text-white">{option.label}</p>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400">{option.description}</p>
                </div>
                <ToggleSwitch
                  enabled={settings[option.key] ?? true}
                  onChange={() => handleToggle(option.key)}
                />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// Toggle Switch Component
function ToggleSwitch({ enabled, onChange }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      onClick={onChange}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 ${
        enabled ? 'bg-primary-500' : 'bg-neutral-200 dark:bg-neutral-600'
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
          enabled ? 'translate-x-5' : 'translate-x-0'
        }`}
      />
    </button>
  );
}

// Change Password Modal
function ChangePasswordModal({ onClose }) {
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const mutation = useMutation({
    mutationFn: (data) => authAPI.changePassword(data),
    onSuccess: () => {
      setSuccess(true);
      setTimeout(onClose, 2000);
    },
    onError: (err) => {
      setError(err.response?.data?.error || 'Failed to change password');
    },
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    setError('');

    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match');
      return;
    }

    if (formData.newPassword.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    mutation.mutate({
      currentPassword: formData.currentPassword,
      newPassword: formData.newPassword,
    });
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-white dark:bg-neutral-800 rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-neutral-200 dark:border-neutral-700">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">Change Password</h3>
        </div>

        {success ? (
          <div className="p-6 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-neutral-900 dark:text-white">Password Changed</p>
            <p className="text-neutral-500 dark:text-neutral-400">Your password has been updated successfully.</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg flex items-center gap-2 text-red-700 dark:text-red-300">
                <AlertCircle className="w-4 h-4" />
                {error}
              </div>
            )}

            <div>
              <label className="label">Current Password</label>
              <input
                type="password"
                className="input"
                value={formData.currentPassword}
                onChange={(e) => setFormData({ ...formData, currentPassword: e.target.value })}
                required
              />
            </div>

            <div>
              <label className="label">New Password</label>
              <input
                type="password"
                className="input"
                value={formData.newPassword}
                onChange={(e) => setFormData({ ...formData, newPassword: e.target.value })}
                required
                minLength={8}
              />
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">Must be at least 8 characters</p>
            </div>

            <div>
              <label className="label">Confirm New Password</label>
              <input
                type="password"
                className="input"
                value={formData.confirmPassword}
                onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                required
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button type="button" className="btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  'Update Password'
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
